import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { ForkEffect, call, put, takeLatest } from 'redux-saga/effects';
import { actionsTracking } from '@/reducers/tracking/tracking.slice';
import {
  getPointDetailApi,
  getTrackingInfoApi,
} from '@/services/TrackingServices';
import { TrackingRequest } from '@/services/types';
import { $loader } from '@/app/components/Loader/Loader';
import i18next from 'i18next';
import { $alerts } from '@pickit/pickit-components';
import { store } from '@/reducers/store';
import { PointInfo, Status, TrackingBaseData } from '@/reducers/tracking/types';
import { routes } from '@/constants/routes';
import { push } from 'connected-react-router';
import pushDataLayer from '@/utils/dataLayer';
import { NOT_FOUND } from '@/constants/dataLayer';
import { SagaResponse } from './type';

function* getTracking(action: PayloadAction<TrackingRequest>) {
  $loader.next(true);
  const response: AxiosResponse<SagaResponse<TrackingBaseData>> = yield call(
    getTrackingInfoApi,
    action.payload,
  );
  $loader.next(false);
  const { dispatch } = store;
  if (response.status !== 200) {
    yield put(actionsTracking.getTrackingError());
    pushDataLayer(NOT_FOUND);

    $loader.next(false);
    $alerts.next({
      type: response?.status === 400 ? 'info' : 'error',
      title: response?.data?.context ?? i18next.t('Home:genericError'),
      subMessage: response?.data?.context ?? '',
      onClose: () => {
        dispatch(actionsTracking.setPickitCode(''));
        dispatch(actionsTracking.setIsDirty(false));
      },
      onConfirmLabel: i18next.t('Home:inactiveButton'),
    });
  } else {
    if (!response.data) {
      $alerts.next({
        type: 'error',
        title: response.headers
          ? decodeURIComponent(escape(response.headers['context-message']))
          : i18next.t('Home:genericError'),
        onClose: () => dispatch(actionsTracking.setPickitCode('')),
      });
      yield put(actionsTracking.getTrackingError());

      return;
    }

    if (!response.data.title) {
      $alerts.next({
        title: i18next.t('TrackingInfo:noStateTitle'),
        type: 'warning',
        subMessage: i18next.t('TrackingInfo:noStateMessage'),
        onConfirmLabel: i18next.t('TrackingInfo:noStateLabel'),
        onClose: () => dispatch(actionsTracking.setPickitCode('')),
      });
      yield put(actionsTracking.getTrackingError());

      return;
    }
    yield put(
      actionsTracking.getTrackingSuccess({
        ...response.data,
        tracking: response.data.tracking.reverse(),
      }),
    );
    yield put(actionsTracking.setToken(action.payload.retailerToken));
    if (response.data.transaction.status === Status.Live) {
      yield put(push(routes.TRACKING_LIVE_INFO));
    } else {
      yield put(push(routes.TRACKING_INFO));
    }
  }
}

function* getPointInfo(action: PayloadAction<number>) {
  const response: AxiosResponse<SagaResponse<PointInfo>> = yield call(
    getPointDetailApi,
    action.payload,
  );
  if (response.status !== 200) {
    yield put(actionsTracking.getPointInfoError());
  } else {
    yield put(actionsTracking.getPointInfoSuccess(response.data));
  }
}

const sagas: ForkEffect<never>[] = [
  takeLatest(actionsTracking.getTrackingRequest.type, getTracking),
  takeLatest(actionsTracking.getPointInfoRequest.type, getPointInfo),
];

export default sagas;
